import { useState } from 'react';
import Modal from '../../common/Modal';
import { Copy, Check, AlertTriangle } from 'lucide-react';
import Button from '../../ui/button/Button';

interface Domain {
    id: string;
    domain: string;
    cnameRecord: string;
    verified: boolean;
}

interface DnsInstructionsModalProps {
    isOpen: boolean;
    onClose: () => void;
    domain: Domain;
    onVerify: () => void;
}

export default function DnsInstructionsModal({ isOpen, onClose, domain, onVerify }: DnsInstructionsModalProps) {
    const [copiedCname, setCopiedCname] = useState(false);

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        setCopiedCname(true);
        setTimeout(() => setCopiedCname(false), 2000);
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size="lg"
        >
            <div className="p-6">
                <h2 className="text-xl font-semibold text-gray-800 dark:text-white/90 mb-4">
                    Instruções para Configuração de DNS
                </h2>

                <div className="mb-6">
                    <h3 className="text-lg font-medium text-gray-800 dark:text-white/90 mb-3">
                        Para ativar seu domínio, adicione um registro CNAME:
                    </h3>
                </div>

                {/* Registro CNAME */}
                <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="flex items-start justify-between mb-3">
                        <div>
                            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                Adicione um registro CNAME no seu provedor de DNS:
                            </h4>
                        </div>
                    </div>

                    <div className="space-y-3">
                        <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-700">
                            <div className="flex-1">
                                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">
                                    Nome:
                                </span>
                                <code className="text-sm text-gray-800 dark:text-white/90 font-mono">
                                    tracking
                                </code>
                            </div>
                            <button
                                onClick={() => copyToClipboard('tracking')}
                                className="ml-3 p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                                title="Copiar"
                            >
                                {copiedCname ? <Check className="size-4 text-green-500" /> : <Copy className="size-4" />}
                            </button>
                        </div>

                        <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-700">
                            <div className="flex-1">
                                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">
                                    Tipo:
                                </span>
                                <code className="text-sm text-gray-800 dark:text-white/90 font-mono">
                                    CNAME
                                </code>
                            </div>
                            <button
                                onClick={() => copyToClipboard('CNAME')}
                                className="ml-3 p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                                title="Copiar"
                            >
                                {copiedCname ? <Check className="size-4 text-green-500" /> : <Copy className="size-4" />}
                            </button>
                        </div>

                        <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-700">
                            <div className="flex-1">
                                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">
                                    Valor:
                                </span>
                                <code className="text-sm text-gray-800 dark:text-white/90 font-mono">
                                    {domain.cnameRecord}
                                </code>
                            </div>
                            <button
                                onClick={() => copyToClipboard(domain.cnameRecord)}
                                className="ml-3 p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                                title="Copiar"
                            >
                                {copiedCname ? <Check className="size-4 text-green-500" /> : <Copy className="size-4" />}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Aviso importante */}
                <div className="flex items-start gap-3 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg mb-6">
                    <AlertTriangle className="size-5 text-yellow-600 dark:text-yellow-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                        <p className="text-sm text-yellow-800 dark:text-yellow-200 font-medium mb-1">
                            Importante:
                        </p>
                        <p className="text-sm text-yellow-700 dark:text-yellow-300">
                            As alterações de DNS podem levar até 24 horas para propagar. Após configurar os registros, 
                            clique no botão "Verificar" abaixo para confirmar as alterações.
                        </p>
                    </div>
                </div>

                {/* Botões de ação */}
                <div className="flex justify-end gap-3">
                    <Button
                        size="md"
                        variant="outline"
                        onClick={onClose}
                    >
                        Fechar
                    </Button>
                    <Button
                        size="md"
                        variant="primary"
                        onClick={onVerify}
                    >
                        Verificar
                    </Button>
                </div>
            </div>
        </Modal>
    );
}
